import DicomDict2 from "../types/DicomDict2";
import Instance from "../types/Instance";
import isColorImage from "../isColorImage";
import { TypedArray } from "../types/ImageFrame";

const EXPLICIT_VR_LE = "1.2.840.10008.1.2.1";
export const PIXEL_DATA_TAG = "7FE00010";

/**
 * After frame-level trimming, the surviving `instance.frames` may be a subset
 * of the frames the dataset's per-frame metadata describes. Slice the
 * frame-indexed sequences down to the kept frames so the written P10 stays
 * internally consistent. `keptIndices` are the original 0-based frame indices,
 * in output order.
 */
export function slicePerFrameSequences(dataset: DicomDict2, keptIndices: number[]) {
	// PerFrameFunctionalGroupsSequence — one item per frame (enhanced CT/MR/US).
	const perFrameGroups = dataset.dict["52009230"];
	if (perFrameGroups?.Value && keptIndices.length !== perFrameGroups.Value.length) {
		perFrameGroups.Value = keptIndices.map((i) => perFrameGroups.Value[i]);
	}
	// FrameTimeVector — one timing value per frame (legacy cine, e.g. US).
	const frameTimeVector = dataset.dict["00181065"];
	if (frameTimeVector?.Value && keptIndices.length !== frameTimeVector.Value.length) {
		frameTimeVector.Value = keptIndices.map((i) => frameTimeVector.Value[i]);
	}
}

/** Drop the canvas alpha channel from a decoded color frame (RGBA -> RGB). */
export function removeAlphaChannel(pixelArray: TypedArray): Uint8Array {
	const newPixelArray = new Uint8Array(pixelArray.length * 0.75);
	let targetPosition = 0;
	for (let srcPosition = 0; srcPosition < pixelArray.length; srcPosition += 1) {
		if ((srcPosition + 1) % 4 === 0) {
			continue;
		}
		newPixelArray[targetPosition] = pixelArray[srcPosition];
		targetPosition += 1;
	}
	return newPixelArray;
}

/**
 * Mutate the instance's anonymized dataset into its final state *before* pixel
 * data: explicit-LE transfer syntax, color photometric tags, per-frame sequence
 * slicing to match any trim, NumberOfFrames, and the pixel-data tag removed.
 *
 * Shared by the monolithic writer (`writeInstanceToBuffer`) and the streaming
 * writer (`streamCompressInstance`) so both emit byte-identical headers; the only
 * difference downstream is how the pixel element is appended.
 */
export function prepareAnonymizedDataset(instance: Instance): {
	dataset: DicomDict2;
	isColor: boolean;
} {
	const { anonymizedDicomData } = instance.instanceData;
	if (!anonymizedDicomData) {
		throw new Error(
			`Instance has not been successfully anonymized. Instance ${JSON.stringify(
				instance
			)}`
		);
	}

	// TODO: #1 Find out why writing to implicit LE doesn't work correctly
	anonymizedDicomData.meta["00020010"].Value = [EXPLICIT_VR_LE];

	const isColor = isColorImage(anonymizedDicomData);
	if (isColor) {
		anonymizedDicomData.upsertTag("00280004", "CS", ["RGB"]);
		anonymizedDicomData.upsertTag("00280002", "US", [3]);
		anonymizedDicomData.upsertTag("00280006", "US", [0]);
	}

	// Keep frame-indexed metadata + NumberOfFrames in sync with any trim. Uses the
	// original frame indices of the surviving frames so it stays correct for any
	// subset. Single-frame instances keep all (one) frames, so this is a no-op.
	const keptIndices = instance.frames.map((frame) => frame.originalFrameIndex);
	slicePerFrameSequences(anonymizedDicomData, keptIndices);
	if (instance.frames.length > 1 || anonymizedDicomData.dict["00280008"]) {
		anonymizedDicomData.upsertTag("00280008", "IS", [
			instance.frames.length.toString(),
		]);
	}

	// The dataset is parsed only up to (not including) pixel data at load time, so
	// it normally has no pixel tag — but a prior write may have left one. Ensure
	// it's absent; both writers append the pixel element themselves.
	delete anonymizedDicomData.dict[PIXEL_DATA_TAG];

	return { dataset: anonymizedDicomData, isColor };
}
